import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ProductCard from './ProductCard';
import { fetchStoreProducts } from '../Api/storeApi';
import '../assets/css/category-rail.css';

/**
 * One horizontal shelf of products for a single category.
 *
 * Replaces the home page's single "Featured products" grid, which showed the
 * first 12 products of the whole catalogue — and since the catalogue is ordered
 * by date, that meant 12 arbitrary products with no relationship to each other.
 *
 * PERFORMANCE — this is the part that needs care. A rail per category is a
 * request per category, and there are 16 stocked ones. Fetching them all on
 * load would reintroduce exactly what PERF-1 removed: the largest cost in the
 * app spent on the landing page, most of it never seen.
 *
 * So each rail fetches its own products ONLY when it is about to scroll into
 * view, via IntersectionObserver with a 300px margin. The first two rails pass
 * `eager` because they are above the fold and would otherwise flash empty. A
 * visitor who lands and leaves pays for two requests of 10 products, not
 * sixteen — and the requests are unauthenticated Store API calls, so they are
 * cacheable and carry no credential.
 */

const PRODUCTS_PER_RAIL = 10;

export default function CategoryRail({ category, isAuthenticated, onAddToCart, eager = false }) {
    const [products, setProducts] = useState([]);
    const [status, setStatus] = useState('idle'); // idle | loading | ready | failed
    const [overflow, setOverflow] = useState({ left: false, right: false });

    const sectionRef = useRef(null);
    const trackRef = useRef(null);

    // Which category id this rail has already requested. A ref, NOT state, and
    // that is the whole point — see the warning below.
    const requestedForRef = useRef(null);

    // --- fetch, once, when the rail is about to become visible -------------

    /* DO NOT put `status` in this effect's dependency array.
     *
     * The first version did, and guarded with `if (status !== 'idle') return`.
     * That makes the effect cancel itself: `load()` calls setStatus('loading'),
     * the state change re-runs the effect, and React fires the PREVIOUS run's
     * cleanup first — which set `cancelled = true`. The in-flight request then
     * resolved into an early return, so setProducts and setStatus('ready')
     * never ran and every rail sat on its skeletons forever.
     *
     * It failed in the most expensive way available: the request succeeded, the
     * response was correct, the network tab was clean, and nothing threw. The
     * only symptom was permanent placeholders.
     *
     * Deduplication belongs in a ref precisely because a ref does not re-trigger
     * the effect that reads it. There is no `cancelled` flag any more either —
     * React 18 made setState on an unmounted component a silent no-op, so the
     * flag bought nothing and cost this bug.
     */
    useEffect(() => {
        const load = async () => {
            if (requestedForRef.current === category.id) return;
            requestedForRef.current = category.id;

            setStatus('loading');
            try {
                const { data } = await fetchStoreProducts({
                    category: category.id,
                    per_page: PRODUCTS_PER_RAIL,
                    page: 1,
                });
                setProducts(data);
                setStatus('ready');

                // An empty shelf and a broken query look identical to a
                // customer, so say which this is. The rail hides itself either
                // way — a row of nothing is worse than no row — but a silent
                // disappearance is how `/surgical` stayed broken unnoticed.
                if (data.length === 0) {
                    console.warn(
                        `CategoryRail: category "${category.slug}" (id ${category.id}) returned no products ` +
                        `though the catalogue reports ${category.count}.`
                    );
                }
            } catch (error) {
                console.error(`CategoryRail: failed to load "${category.slug}"`, error);
                setStatus('failed');
            }
        };

        if (eager || typeof IntersectionObserver === 'undefined') {
            load();
            return undefined;
        }

        const node = sectionRef.current;
        if (!node) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    observer.disconnect();
                    load();
                }
            },
            { rootMargin: '300px 0px' }
        );
        observer.observe(node);

        return () => observer.disconnect();
    }, [eager, category.id, category.slug, category.count]);

    // --- arrow visibility --------------------------------------------------

    const syncOverflow = useCallback(() => {
        const track = trackRef.current;
        if (!track) return;
        const maxScroll = track.scrollWidth - track.clientWidth;
        setOverflow({
            left: track.scrollLeft > 8,
            // 8px of slack: sub-pixel widths mean scrollLeft rarely reaches
            // maxScroll exactly, which would leave a dead arrow on screen.
            right: track.scrollLeft < maxScroll - 8,
        });
    }, []);

    useEffect(() => {
        if (status !== 'ready') return undefined;
        syncOverflow();
        window.addEventListener('resize', syncOverflow);
        return () => window.removeEventListener('resize', syncOverflow);
    }, [status, products.length, syncOverflow]);

    const scrollBy = (direction) => {
        const track = trackRef.current;
        if (!track) return;
        // Scroll by a viewport-width of cards rather than a fixed number, so
        // the control does the same thing on a phone and a wide desktop.
        track.scrollBy({ left: direction * track.clientWidth * 0.9, behavior: 'smooth' });
    };

    // A failed or empty rail renders nothing at all. The console carries the
    // reason; the customer should not see a labelled hole.
    if (status === 'failed' || (status === 'ready' && products.length === 0)) {
        return null;
    }

    return (
        <section className="cat-rail" ref={sectionRef} aria-labelledby={`rail-${category.id}`}>
            <div className="cat-rail__head">
                <div className="cat-rail__heading">
                    {category.image?.src ? (
                        <img
                            className="cat-rail__icon"
                            src={category.image.src}
                            alt=""
                            loading="lazy"
                            aria-hidden="true"
                        />
                    ) : null}
                    <h2 className="cat-rail__title" id={`rail-${category.id}`}>{category.name}</h2>
                    <span className="cat-rail__count">{category.count}</span>
                </div>
                <Link className="cat-rail__all" to={`/category/${category.slug}`}>
                    View all
                </Link>
            </div>

            <div className="cat-rail__frame">
                {overflow.left && (
                    <button
                        type="button"
                        className="cat-rail__arrow cat-rail__arrow--left"
                        onClick={() => scrollBy(-1)}
                        aria-label={`Scroll ${category.name} left`}
                    >
                        &#8249;
                    </button>
                )}

                <div className="cat-rail__track" ref={trackRef} onScroll={syncOverflow}>
                    {status === 'ready'
                        ? products.map((product) => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                isAuthenticated={isAuthenticated}
                                onAddToCart={onAddToCart}
                            />
                        ))
                        // Placeholders rather than a spinner: they reserve the
                        // row's height, so rails below do not jump upward as
                        // each one resolves.
                        : Array.from({ length: 5 }, (_, i) => (
                            <div className="cat-rail__skeleton" key={`s${i}`} aria-hidden="true" />
                        ))}
                </div>

                {overflow.right && (
                    <button
                        type="button"
                        className="cat-rail__arrow cat-rail__arrow--right"
                        onClick={() => scrollBy(1)}
                        aria-label={`Scroll ${category.name} right`}
                    >
                        &#8250;
                    </button>
                )}
            </div>
        </section>
    );
}
